import {
  AppDrawer,
  ColorDynamic,
  Column,
  Columns,
  Inline,
  OverflowText,
  Stack,
  useSnackbarStack,
} from '@superdispatch/ui';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import {
  SHIPMENT_COUNT_QUERY_KEY,
  SHIPMENT_LIST_QUERY_KEY,
  SUBHAULED_LOAD_COUNT_QUERY_KEY,
  useShipmentSubhaulPreview,
  useSubhaulAPI,
} from '../data/VehiclesAPI';
import {
  ShipmentSubhaulPreviewDTO,
  SubhaulResponseDTO,
  VehicleFormDTO,
  vehicleSubhaulSchema,
} from '../data/VehiclesDTO';
import { VehicleSubhaulProgress } from './VehicleSubhaulProgress';
import { VehicleSubhaulLoad } from './VehicleSubhaulLoad';
import { Typography } from '@material-ui/core';
import { VehicleSubhaulDrawerLoader } from './VehicleSubhaulDrawerLoader';
import { VehicleCarrierSearch } from './VehicleCarrierSearch';
import { Form, FormikProvider } from 'formik';
import { VehicleSubhaulPrice } from './VehicleSubhaulPrice';
import { joinStrings } from 'shared/utils/StringUtils';
import { Box, Button } from '@superdispatch/ui-lab';
import { DottedLineRow } from 'shared/ui/DottedLine';
import { formatCurrency, formatPlural } from 'shared/helpers/IntlHelpers';
import { formatCarrierPaymentMethod } from 'shared/helpers/CarrierPaymentTypes';
import { formatPaymentTerm } from '@superdispatch/sdk';
import { useAppFormik } from 'shared/form/AppFormik';
import { useQueryClient } from '@tanstack/react-query';
import { LoadDTO } from 'carrier/loads/data/LoadDTO';
import { useLoadsAPI } from 'carrier/loads/data/LoadsAPI';
import { Info } from '@material-ui/icons';

const FORM_ID = 'vehicle-subhaul-drawer-form';

interface VehicleSubhaulDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  load?: LoadDTO;
  isLoadPending?: boolean;
}

export function VehicleSubhaulDrawer({
  isOpen,
  onClose,
  load,
  isLoadPending,
}: VehicleSubhaulDrawerProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { addSnackbar, clearStack } = useSnackbarStack();
  const [searchParams] = useSearchParams();
  const guid = searchParams.get('guid');
  const bundle = searchParams.get('bundle');
  const isBundled = bundle === 'true';
  const [currentShipmentIndex, setShipmentIndex] = useState(0);
  const subhaulPriceInputRef = useRef<HTMLInputElement>(null);

  const { submitSubhaul } = useSubhaulAPI();
  const { subhaulLoad } = useLoadsAPI();
  const { data, isLoading, isSuccess } = useShipmentSubhaulPreview(
    {
      guid,
      bundle: isBundled,
    },
    {
      enabled: isOpen && !load?.guid,
    },
  );

  const currentShipment = data?.[currentShipmentIndex];
  const shipmentCount = useMemo(
    () => (load?.guid ? 1 : (data?.length ?? 0)),
    [data, load?.guid],
  );

  const drawerTitle = useMemo(() => {
    if (load?.customer.venue.name) {
      return `for ${load.customer.venue.name}`;
    }
    if (!currentShipment?.customer) return '';
    return `for ${currentShipment.customer}`;
  }, [currentShipment?.customer, load?.customer.venue.name]);

  const currentShipmentGuids = useMemo(() => {
    return (
      currentShipment?.vehicles?.map((vehicle) => vehicle.shipment_guid) ?? []
    );
  }, [currentShipment?.vehicles]);

  const formik = useAppFormik<VehicleFormDTO, SubhaulResponseDTO[]>({
    validationSchema: vehicleSubhaulSchema,
    initialValues: {
      carrier_guid: '',
      payment_method: '',
      payment_terms: '',
      subhauling_price: null,
      original_price: currentShipment?.price || 0,
    },
    onSubmit: (payload) => {
      if (load?.guid) {
        return subhaulLoad(load.guid, {
          original_price: payload.original_price,
          payment_method: payload.payment_method,
          payment_terms: payload.payment_terms,
          subhauling_price: payload.subhauling_price,
          carrier_guid: payload?.carrier_guid || '',
        });
      }
      return submitSubhaul({
        original_price: payload.original_price,
        payment_method: payload.payment_method,
        payment_terms: payload.payment_terms,
        subhauling_price: payload.subhauling_price,
        carrier_guid: payload?.carrier_guid || '',
        shipment_guids: currentShipmentGuids,
      });
    },
    onSubmitSuccess: () => {
      formik.resetForm();
      setShipmentIndex((prev) => prev + 1);
      subhaulPriceInputRef.current?.focus();
      void queryClient.invalidateQueries({
        queryKey: SHIPMENT_LIST_QUERY_KEY,
      });
      void queryClient.invalidateQueries({
        queryKey: SHIPMENT_COUNT_QUERY_KEY,
      });
      void queryClient.invalidateQueries({
        queryKey: SUBHAULED_LOAD_COUNT_QUERY_KEY,
      });
      if (currentShipmentIndex + 1 >= shipmentCount) {
        setShipmentIndex(0);
        onClose();
        addSnackbar(
          `${shipmentCount} sub-haul ${formatPlural(shipmentCount, 'offer was', 'offers were')} successfully sent`,
          {
            variant: 'success',
            autoHideDuration: 10000,
            action: (
              <Button
                variant="inverted"
                onClick={() => {
                  navigate({
                    pathname: '/vehicles/subhauled',
                    search: '?status=all',
                  });
                  clearStack();
                }}
              >
                Show in Loads
              </Button>
            ),
          },
        );
      }
    },
  });
  const { values } = formik;

  const [vehicle] = currentShipment?.vehicles ?? [];
  const subhauledVehicle = joinStrings(
    ' ',
    vehicle?.year,
    vehicle?.make,
    vehicle?.model,
  );

  const disabled =
    isLoadPending ||
    isLoading ||
    shipmentCount === 0 ||
    !values.subhauling_price ||
    !values.payment_method ||
    !values.payment_terms;
  const subhaulPrice = joinStrings(
    ' · ',
    values.subhauling_price != null &&
      formatCurrency(values.subhauling_price, {
        maximumFractionDigits: 1,
      }),
    values.payment_method && formatCarrierPaymentMethod(values.payment_method),
    values.payment_terms && formatPaymentTerm(values.payment_terms),
  );

  useEffect(() => {
    if (data?.length && isSuccess) {
      setShipmentIndex(0);
    }
  }, [data, isSuccess]);

  return (
    <AppDrawer
      size="lg"
      title={
        <Inline verticalAlign="center" space="xxsmall">
          <Typography variant="h3">Sub-haul</Typography>
          <Typography variant="h3" color="textSecondary">
            {drawerTitle}
          </Typography>
        </Inline>
      }
      open={isOpen}
      onClose={() => {
        onClose();
        formik.resetForm();
        setShipmentIndex(0);
      }}
      disableContentPadding={true}
      footer={
        <Box paddingTop="medium" paddingBottom="medium" width="100%">
          <Stack
            space={
              values.carrier_name || subhaulPrice.length > 0 ? 'large' : 'none'
            }
          >
            <Stack space="small">
              {values.carrier_name && (
                <DottedLineRow
                  title="Carrier"
                  variant="body2"
                  value={
                    <Box maxWidth="400px">
                      <OverflowText disableUnderline={true}>
                        {values.carrier_name}
                      </OverflowText>
                    </Box>
                  }
                />
              )}
              {subhaulPrice.length > 0 && (
                <DottedLineRow
                  title="Sub-haul Price"
                  variant="body2"
                  value={
                    <Typography variant="body1">{subhaulPrice}</Typography>
                  }
                />
              )}
            </Stack>
            <Button
              disabled={disabled}
              pending={formik.isSubmitting}
              onClick={() => {
                if (!values.carrier_guid) {
                  addSnackbar('Please select a carrier before sub-hauling.', {
                    variant: 'error',
                  });
                  return;
                }
                formik.handleSubmit();
              }}
            >
              Sub-haul {subhauledVehicle}
            </Button>
          </Stack>
        </Box>
      }
    >
      <FormikProvider value={formik}>
        <Form id={FORM_ID}>
          <SubhaulDrawerContent
            ref={subhaulPriceInputRef}
            load={load}
            data={data}
            isLoading={isLoading || isLoadPending}
            currentShipmentIndex={currentShipmentIndex}
          />
        </Form>
      </FormikProvider>
    </AppDrawer>
  );
}

interface SubhaulDrawerContentProps {
  data?: ShipmentSubhaulPreviewDTO[] | null;
  isLoading?: boolean;
  currentShipmentIndex: number;
  load?: LoadDTO;
}

const SubhaulDrawerContent = forwardRef<
  HTMLInputElement,
  SubhaulDrawerContentProps
>(function SubhaulDrawerContent(
  { data, isLoading, currentShipmentIndex, load },
  subhaulPriceInputRef,
) {
  const shipment = data?.[currentShipmentIndex];
  const shipmentCount = load?.guid ? 1 : (data?.length ?? 0);

  if (isLoading) {
    return <VehicleSubhaulDrawerLoader />;
  }

  return (
    <Stack space="small">
      <VehicleSubhaulProgress
        shipmentCount={shipmentCount}
        currentShipmentIndex={currentShipmentIndex}
      />
      {!!load?.guid && (
        <Box paddingLeft="large" paddingRight="large">
          <Box
            width="100%"
            padding="xsmall"
            borderRadius="small"
            backgroundColor="Blue50"
          >
            <Columns space="xsmall" align="top">
              <Column width="content">
                <Info fontSize="small" htmlColor={ColorDynamic.Blue300} />
              </Column>
              <Column width="fluid">
                <Typography>
                  This load's inspection details and status history will be
                  reset. It will appear under Sub-hauled Loads in the Vehicles
                  page.
                </Typography>
              </Column>
            </Columns>
          </Box>
        </Box>
      )}
      <VehicleSubhaulLoad
        load={load}
        shipment={shipment}
        shipmentCount={shipmentCount}
      />
      <VehicleSubhaulPrice ref={subhaulPriceInputRef} />
      <VehicleCarrierSearch />
    </Stack>
  );
});
